// components/VariantReport.tsx
import React, { useState } from 'react';
import { VariantData } from '../types';
import ProteinStructureViewer from './ProteinStructureViewer';
import VariantGalaxy from './VariantGalaxy';

interface VariantReportProps {
  data: VariantData;
}

const VariantReport: React.FC<VariantReportProps> = ({ data }) => {
  // Removed 'transcripts' from the tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'clinical' | 'population' | 'structure'>('overview');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [structureView, setStructureView] = useState<'classic' | 'galaxy'>('classic');

  // ===========================================
  // Helper Functions
  // ===========================================

  // Get color based on pathogenicity
  const getPathogenicityColor = (): string => {
    // For LoF variants, use constraint scores
    if (data.isLofVariant) {
      if (data.lofScores?.pLI && data.lofScores.pLI > 0.9) return 'red';
      if (data.lofScores?.loeuf && data.lofScores.loeuf < 0.35) return 'red';
      return 'yellow';
    }
    // For missense variants, use AlphaMissense
    if (data.alphaMissenseScore === -1) return 'gray';
    if (data.alphaMissenseClass.toLowerCase().includes('pathogenic')) return 'red';
    if (data.alphaMissenseClass.toLowerCase().includes('benign')) return 'green';
    return 'yellow';
  };

  const pathColor = getPathogenicityColor();

  const renderLinkedSummary = (text: string) => text
    .split(/(https:\/\/pubmed\.ncbi\.nlm\.nih\.gov\/\d+\/?)/g)
    .map((part, index) => (
      part.startsWith('https://pubmed.ncbi.nlm.nih.gov/') ? (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.externalLink}
        >
          {part}
        </a>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    ));

  // Get classification text based on variant type
  const getClassificationText = (): string => {
    if (data.isLofVariant) {
      if (data.lofScores?.pLI && data.lofScores.pLI > 0.9) {
        return 'Loss-of-Function (LoF Intolerant Gene)';
      }
      return 'Loss-of-Function';
    }
    return data.alphaMissenseClass;
  };

  // Helper to format score with color
  const getScoreStyle = (
    score: number | null,
    type: 'alphamissense' | 'sift' | 'polyphen' | 'cadd' | 'revel'
  ): React.CSSProperties => {
    if (score === null || score === -1) return { color: '#9ca3af' };

    if (type === 'alphamissense') {
      if (score >= 0.564) return { color: '#dc2626', fontWeight: 600 };
      if (score <= 0.34) return { color: '#16a34a', fontWeight: 600 };
      return { color: '#ca8a04', fontWeight: 600 };
    }
    if (type === 'sift') {
      if (score < 0.05) return { color: '#dc2626', fontWeight: 600 };
      return { color: '#16a34a', fontWeight: 600 };
    }
    if (type === 'polyphen') {
      if (score > 0.908) return { color: '#dc2626', fontWeight: 600 };
      if (score > 0.446) return { color: '#ca8a04', fontWeight: 600 };
      return { color: '#16a34a', fontWeight: 600 };
    }
    if (type === 'cadd') {
      if (score > 20) return { color: '#dc2626', fontWeight: 600 };
      if (score > 15) return { color: '#ca8a04', fontWeight: 600 };
      return { color: '#16a34a', fontWeight: 600 };
    }
    if (type === 'revel') {
      if (score > 0.75) return { color: '#dc2626', fontWeight: 600 };
      if (score > 0.5) return { color: '#ca8a04', fontWeight: 600 };
      return { color: '#16a34a', fontWeight: 600 };
    }
    return {};
  };

  // Get pLI color
  const getPliColor = (pli: number | null): React.CSSProperties => {
    if (pli === null) return { color: '#9ca3af' };
    if (pli > 0.9) return { color: '#dc2626', fontWeight: 700 };
    if (pli > 0.5) return { color: '#ca8a04', fontWeight: 600 };
    return { color: '#16a34a', fontWeight: 600 };
  };

  // Get LOEUF color
  const getLoeufColor = (loeuf: number | null): React.CSSProperties => {
    if (loeuf === null) return { color: '#9ca3af' };
    if (loeuf < 0.35) return { color: '#dc2626', fontWeight: 700 };
    if (loeuf < 0.6) return { color: '#ca8a04', fontWeight: 600 };
    return { color: '#16a34a', fontWeight: 600 };
  };

  // Format frequency as percentage
  const formatFrequency = (freq: number | null | undefined): string => {
    if (freq === null || freq === undefined) return 'N/A';
    if (freq === 0) return '0%';
    if (freq < 0.0001) return '<0.01%';
    return (freq * 100).toFixed(4) + '%';
  };

  // Get CADD interpretation
  const getCaddInterpretation = (phred: number | null): string => {
    if (phred === null) return 'N/A';
    if (phred > 20) return 'Likely Deleterious';
    if (phred > 15) return 'Possibly Deleterious';
    return 'Likely Benign';
  };

  // Get REVEL interpretation
  const getRevelInterpretation = (score: number | null): string => {
    if (score === null) return 'N/A';
    if (score > 0.75) return 'Likely Pathogenic';
    if (score > 0.5) return 'Uncertain';
    return 'Likely Benign';
  };

  // ===========================================
  // Get maximum population frequency
  // ===========================================
  const getMaxPopulationFrequency = (): { maxFreq: number; population: string | null } => {
    const populations = data.populationFrequencies?.gnomadPopulations;
    const globalAF = data.populationFrequencies?.globalAF ?? 0;

    if (!populations) {
      return { maxFreq: globalAF, population: null };
    }

    const popNames: Record<string, string> = {
      afr: 'African/African American',
      amr: 'American (Latino)',
      asj: 'Ashkenazi Jewish',
      eas: 'East Asian',
      fin: 'Finnish',
      nfe: 'Non-Finnish European',
      sas: 'South Asian',
    };

    let maxFreq = globalAF;
    let maxPop: string | null = null;

    for (const [key, value] of Object.entries(populations)) {
      if (value != null && value > maxFreq) {
        maxFreq = value;
        maxPop = popNames[key] || key;
      }
    }

    return { maxFreq, population: maxPop };
  };

  // ===========================================
  // Get frequency interpretation text
  // ===========================================
  const getFrequencyInterpretation = (): string => {
    const { maxFreq, population } = getMaxPopulationFrequency();

    if (maxFreq < 0.0001) {
      return 'This is a rare variant (AF < 0.01% in all populations), which is consistent with pathogenic variants for Mendelian diseases.';
    } else if (maxFreq < 0.01) {
      const popNote = population ? `(highest in ${population})` : '';
      return `This is an uncommon variant (max AF: ${(maxFreq * 100).toFixed(4)}%${popNote}). Consider population-specific frequency when assessing pathogenicity.`;
    } else {
      const popNote = population ? `(highest in ${population})` : '';
      return `This variant has a relatively common population frequency (max AF: ${(maxFreq * 100).toFixed(2)}%${popNote}), which may suggest it is benign or has low penetrance.`;
    }
  };

  // ===========================================
  // Extract variant position for structure viewer
  // ===========================================
  const getVariantPosition = (): number | null => {
    const match = data.proteinChange?.match(/\d+/);
    return match ? parseInt(match[0]) : null;
  };

  return (
    <div style={styles.container}>
      {/* ===========================================
          Header Section
          =========================================== */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.geneInfo}>
            <h1 style={styles.geneName}>{data.gene}</h1>
            <span style={styles.proteinChange}>{data.proteinChange}</span>
            <span style={styles.variantTypeBadge}>{data.variantType}</span>
          </div>
          <div
            style={{
              ...styles.classificationBadge,
              backgroundColor:
                pathColor === 'red'
                  ? '#fef2f2'
                  : pathColor === 'green'
                  ? '#f0fdf4'
                  : pathColor === 'yellow'
                  ? '#fefce8'
                  : '#f9fafb',
              color:
                pathColor === 'red'
                  ? '#dc2626'
                  : pathColor === 'green'
                  ? '#16a34a'
                  : pathColor === 'yellow'
                  ? '#ca8a04'
                  : '#6b7280',
              borderColor:
                pathColor === 'red'
                  ? '#fecaca'
                  : pathColor === 'green'
                  ? '#bbf7d0'
                  : pathColor === 'yellow'
                  ? '#fef08a'
                  : '#e5e7eb',
            }}
          >
            {getClassificationText()}
          </div>
        </div>
        <div style={styles.headerMeta}>
          <span style={styles.metaItem}>
            <span style={styles.metaIcon}>📍</span>
            {data.genomicCoordinates}
          </span>
          <span style={styles.metaItem}>
            <span style={styles.metaIcon}>🔀</span>
            {data.alleleString}
          </span>
          <span style={styles.metaItem}>
            <span style={styles.metaIcon}>⚡</span>
            Impact: {data.structuralImpact}
          </span>
        </div>
      </div>

      {/* ===========================================
          Main Score Card - Variant Type Specific
          =========================================== */}
      {data.isMissenseVariant ? (() => {
        // When AlphaMissense is unavailable, fall back to REVEL as primary score
        const hasAlphaM = data.alphaMissenseScore !== -1;
        const hasRevel = data.revelScore !== null && data.revelScore !== undefined && data.revelScore > 0;
        const primaryScore: number = hasAlphaM ? data.alphaMissenseScore : (hasRevel ? (data.revelScore ?? -1) : -1);
        const primaryLabel = hasAlphaM ? 'AlphaMissense Score' : (hasRevel ? 'REVEL Score' : 'Pathogenicity Score');
        const primaryTitle = hasAlphaM ? 'AlphaMissense Pathogenicity' : (hasRevel ? 'REVEL Pathogenicity (AlphaMissense N/A)' : 'Pathogenicity');
        const primarySource = hasAlphaM ? data.scoreSource : (hasRevel ? 'dbNSFP / REVEL' : 'N/A');

        // REVEL thresholds: >0.75 pathogenic, >0.5 uncertain, ≤0.5 benign
        const getRevColor = (s: number) => s > 0.75 ? 'red' : s > 0.5 ? 'yellow' : 'green';
        const effectiveColor = hasAlphaM ? pathColor : (hasRevel ? getRevColor(data.revelScore!) : 'gray');
        const effectiveColorHex = effectiveColor === 'red' ? '#dc2626' : effectiveColor === 'green' ? '#16a34a' : effectiveColor === 'yellow' ? '#ca8a04' : '#6b7280';

        // Bar thresholds
        const lowThreshold = hasAlphaM ? '34%' : '50%';
        const highThreshold = hasAlphaM ? '56.4%' : '75%';
        const barLabels = hasAlphaM
          ? ['Benign', 'Ambiguous', 'Pathogenic']
          : ['Likely Benign', 'Uncertain', 'Likely Pathogenic'];

        return (
        <div style={styles.scoreCard}>
          <div style={styles.scoreCardHeader}>
            <h2 style={styles.scoreCardTitle}>{primaryTitle}</h2>
            <span style={styles.scoreSource}>{primarySource}</span>
          </div>
          <div style={styles.scoreDisplay}>
            <div style={styles.scoreValue}>
              <span
                style={{
                  ...styles.scoreNumber,
                  color: effectiveColorHex,
                }}
              >
                {primaryScore !== -1
                  ? primaryScore.toFixed(4)
                  : 'N/A'}
              </span>
              <span style={styles.scoreLabel}>{primaryLabel}</span>
            </div>
            {data.populationFrequencies && (
              <div style={{ minWidth: '220px', padding: '12px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Population Frequencies
                </div>
                <div style={{ display: 'grid', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Global AF</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{formatFrequency(data.populationFrequencies.globalAF)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>gnomAD Exomes</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{formatFrequency(data.populationFrequencies.gnomadExomes)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Max Pop AF</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{formatFrequency(getMaxPopulationFrequency().maxFreq)}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Score Bar */}
            <div style={styles.scoreBarContainer}>
              <div style={styles.scoreBar}>
                <div
                  style={{
                    ...styles.scoreBarFill,
                    width: primaryScore !== -1 ? `${primaryScore * 100}%` : '0%',
                    backgroundColor: primaryScore !== -1 ? effectiveColorHex : '#9ca3af',
                  }}
                />
                <div
                  style={{ ...styles.scoreBarMarker, left: lowThreshold }}
                  title={hasAlphaM ? 'Benign threshold (0.34)' : 'Benign threshold (0.50)'}
                />
                <div
                  style={{ ...styles.scoreBarMarker, left: highThreshold }}
                  title={hasAlphaM ? 'Pathogenic threshold (0.564)' : 'Pathogenic threshold (0.75)'}
                />
              </div>
              <div style={styles.scoreBarLabels}>
                {barLabels.map(l => <span key={l}>{l}</span>)}
              </div>
            </div>
          </div>
        </div>
        );
      })() : data.isLofVariant ? (
        // Loss-of-Function Score Card
        <div style={styles.lofScoreCard}>
          <div style={styles.scoreCardHeader}>
            <h2 style={styles.scoreCardTitle}>Loss-of-Function Analysis</h2>
            <span style={styles.lofBadge}>LoF Variant</span>
          </div>
          <div style={styles.lofScoreDisplay}>
            {/* Gene Constraint Scores */}
            <div style={styles.lofScoreGrid}>
              <div style={styles.lofScoreItem}>
                <span style={styles.lofScoreLabel}>pLI Score</span>
                <span
                  style={{
                    ...styles.lofScoreValue,
                    ...getPliColor(data.lofScores?.pLI ?? null),
                  }}
                >
                  {data.lofScores?.pLI?.toFixed(2) ?? 'N/A'}
                </span>
                <span style={styles.lofScoreHint}>
                  {data.lofScores?.pLI != null
                    ? data.lofScores.pLI > 0.9
                      ? 'Extremely LoF Intolerant'
                      : data.lofScores.pLI > 0.5
                      ? 'LoF Intolerant'
                      : 'LoF Tolerant'
                    : 'Probability of LoF Intolerance'}
                </span>
              </div>
              <div style={styles.lofScoreItem}>
                <span style={styles.lofScoreLabel}>LOEUF</span>
                <span
                  style={{
                    ...styles.lofScoreValue,
                    ...getLoeufColor(data.lofScores?.loeuf ?? null),
                  }}
                >
                  {data.lofScores?.loeuf?.toFixed(2) ?? 'N/A'}
                </span>
                <span style={styles.lofScoreHint}>
                  {data.lofScores?.loeuf != null
                    ? data.lofScores.loeuf < 0.35
                      ? 'Highly Constrained'
                      : data.lofScores.loeuf < 0.6
                      ? 'Constrained'
                      : 'Not Constrained'
                    : 'LoF Observed/Expected Upper Bound'}
                </span>
              </div>
              <div style={styles.lofScoreItem}>
                <span style={styles.lofScoreLabel}>Mechanism</span>
                <span style={styles.lofMechanismText}>
                  {data.lofMechanism || data.molecularMechanism}
                </span>
              </div>
            </div>

            {/* NMD Prediction */}
            {data.nmdPrediction && (
              <div style={styles.nmdSection}>
                <div style={styles.nmdHeader}>
                  <span style={styles.nmdTitle}>
                    Nonsense-Mediated Decay (NMD) Prediction
                  </span>
                  <span
                    style={{
                      ...styles.nmdBadge,
                      backgroundColor: data.nmdPrediction.predictedNmdEscape
                        ? '#fef3c7'
                        : '#dcfce7',
                      color: data.nmdPrediction.predictedNmdEscape
                        ? '#92400e'
                        : '#166534',
                    }}
                  >
                    {data.nmdPrediction.predictedNmdEscape
                      ? 'May Escape NMD'
                      : 'Likely NMD Target'}
                  </span>
                </div>
                <p style={styles.nmdExplanation}>
                  {data.nmdPrediction.explanation}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Generic variant card for other types
        <div style={styles.scoreCard}>
          <div style={styles.scoreCardHeader}>
            <h2 style={styles.scoreCardTitle}>Variant Information</h2>
            <span style={styles.scoreSource}>{data.variantType} Variant</span>
          </div>
          <div style={styles.genericInfo}>
            <p>
              <strong>Mechanism:</strong> {data.molecularMechanism}
            </p>
            <p>
              <strong>Impact:</strong> {data.structuralImpact}
            </p>
          </div>
        </div>
      )}

      {/* ===========================================
          Tab Navigation - Removed 'transcripts' tab
          =========================================== */}
      <div style={styles.tabContainer}>
        <div style={styles.tabNav}>
          {(
            ['overview', 'clinical', 'population', 'structure'] as const
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              onMouseEnter={() => setHoveredTab(tab)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab ? styles.tabButtonActive : {}),
                ...(hoveredTab === tab && activeTab !== tab
                  ? { color: '#4f46e5', backgroundColor: '#f8fafc' }
                  : {}),
              }}
            >
              {tab === 'overview' && '📊 Overview'}
              {tab === 'clinical' && '🏥 Clinical'}
              {tab === 'population' && '👥 Population'}
              {tab === 'structure' && '🧬 Structure'}
            </button>
          ))}
        </div>

        {/* ===========================================
            Tab Content
            =========================================== */}
        <div style={styles.tabContent}>
          {/* ===========================================
              Overview Tab - Now includes Details content
              =========================================== */}
          {activeTab === 'overview' && (
            <div style={styles.overviewGrid}>
              {/* Prediction Scores Card */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🎯</span>
                  {data.isMissenseVariant
                    ? 'Pathogenicity Predictions'
                    : 'Variant Assessment'}
                </h3>
                {data.isMissenseVariant ? (
                  // Missense-specific predictions
                  <div style={styles.predictionGrid}>
                    {/* AlphaMissense */}
                    <div style={styles.predictionItem}>
                      <div style={styles.predictionHeader}>
                        <span style={styles.predictionName}>AlphaMissense</span>
                        <span style={styles.predictionBadge}>AI-based</span>
                      </div>
                      <div style={styles.predictionScore}>
                        <span
                          style={getScoreStyle(
                            data.alphaMissenseScore,
                            'alphamissense'
                          )}
                        >
                          {data.alphaMissenseScore !== -1
                            ? data.alphaMissenseScore.toFixed(4)
                            : 'N/A'}
                        </span>
                        <span style={styles.predictionClass}>
                          {data.alphaMissenseClass}
                        </span>
                      </div>
                      <div style={styles.predictionNote}>
                        ≥0.564 = Pathogenic, ≤0.34 = Benign
                      </div>
                    </div>

                    {/* SIFT */}
                    <div style={styles.predictionItem}>
                      <div style={styles.predictionHeader}>
                        <span style={styles.predictionName}>SIFT</span>
                        <span style={styles.predictionBadge}>Sequence-based</span>
                      </div>
                      <div style={styles.predictionScore}>
                        <span style={getScoreStyle(data.siftScore, 'sift')}>
                          {data.siftScore !== null
                            ? data.siftScore.toFixed(3)
                            : 'N/A'}
                        </span>
                        <span style={styles.predictionClass}>
                          {data.siftPrediction
                            ? data.siftPrediction.replace(/_/g, ' ')
                            : 'N/A'}
                        </span>
                      </div>
                      <div style={styles.predictionNote}>
                        Score {'<'} 0.05 = Deleterious
                      </div>
                    </div>

                    {/* PolyPhen */}
                    <div style={styles.predictionItem}>
                      <div style={styles.predictionHeader}>
                        <span style={styles.predictionName}>PolyPhen-2</span>
                        <span style={styles.predictionBadge}>Structure-based</span>
                      </div>
                      <div style={styles.predictionScore}>
                        <span
                          style={getScoreStyle(data.polyphenScore, 'polyphen')}
                        >
                          {data.polyphenScore !== null
                            ? data.polyphenScore.toFixed(3)
                            : 'N/A'}
                        </span>
                        <span style={styles.predictionClass}>
                          {data.polyphenPrediction
                            ? data.polyphenPrediction.replace(/_/g, ' ')
                            : 'N/A'}
                        </span>
                      </div>
                      <div style={styles.predictionNote}>
                        Score {'>'} 0.908 = Probably Damaging
                      </div>
                    </div>

                    {/* CADD Score */}
                    {data.caddScore && data.caddScore.phred !== null && (
                      <div style={styles.predictionItem}>
                        <div style={styles.predictionHeader}>
                          <span style={styles.predictionName}>CADD</span>
                          <span style={styles.predictionBadge}>Combined</span>
                        </div>
                        <div style={styles.predictionScore}>
                          <span
                            style={getScoreStyle(data.caddScore.phred, 'cadd')}
                          >
                            {data.caddScore.phred.toFixed(1)}
                          </span>
                          <span style={styles.predictionClass}>
                            {getCaddInterpretation(data.caddScore.phred)}
                          </span>
                        </div>
                        <div style={styles.predictionNote}>
                          PHRED {'>'} 20 = top 1% most deleterious
                        </div>
                      </div>
                    )}

                    {/* REVEL Score */}
                    {data.revelScore !== null && data.revelScore !== undefined && (
                      <div style={styles.predictionItem}>
                        <div style={styles.predictionHeader}>
                          <span style={styles.predictionName}>REVEL</span>
                          <span style={styles.predictionBadge}>Ensemble</span>
                        </div>
                        <div style={styles.predictionScore}>
                          <span style={getScoreStyle(data.revelScore, 'revel')}>
                            {data.revelScore.toFixed(3)}
                          </span>
                          <span style={styles.predictionClass}>
                            {getRevelInterpretation(data.revelScore)}
                          </span>
                        </div>
                        <div style={styles.predictionNote}>
                          Score {'>'} 0.75 suggests pathogenicity
                        </div>
                      </div>
                    )}

                    {/* Show message if CADD/REVEL not available */}
                    {data.isMissenseVariant &&
                      (data.caddScore === null || data.caddScore.phred === null) &&
                      data.revelScore === null && (
                        <div
                          style={{
                            ...styles.predictionItem,
                            backgroundColor: '#f8fafc',
                            border: '1px dashed #e2e8f0',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#64748b',
                              textAlign: 'center',
                            }}
                          >
                            <p style={{ margin: '0 0 8px 0' }}>
                              <strong>CADD & REVEL</strong> scores not available
                            </p>
                            <p style={{ margin: 0, fontSize: '11px' }}>
                              These scores may require specific variant
                              coordinates. AlphaMissense provides the primary
                              pathogenicity assessment.
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                ) : data.isLofVariant ? (
                  // LoF-specific assessment
                  <div style={styles.lofAssessment}>
                    <div style={styles.assessmentItem}>
                      <div style={styles.assessmentIcon}>⚠️</div>
                      <div style={styles.assessmentContent}>
                        <h4 style={styles.assessmentTitle}>
                          Loss-of-Function Variant
                        </h4>
                        <p style={styles.assessmentText}>
                          This {data.variantType.toLowerCase()} variant is
                          predicted to cause loss of protein function.
                          {data.isNonsenseVariant &&
                            ' The premature stop codon truncates the protein.'}
                          {data.isFrameshiftVariant &&
                            ' The frameshift disrupts the reading frame.'}
                          {data.isSpliceVariant &&
                            (data.splicePredictions?.spliceAI_maxDelta !== null && data.splicePredictions?.spliceAI_maxDelta !== undefined
                              ? ` SpliceAI max delta score: ${data.splicePredictions.spliceAI_maxDelta.toFixed(2)}${data.splicePredictions.spliceAI_maxDelta >= 0.8 ? ' (high confidence splice disruption)' : data.splicePredictions.spliceAI_maxDelta >= 0.5 ? ' (moderate splice effect)' : ''}.`
                              : ' The splice site alteration may cause abnormal mRNA processing.')}
                        </p>
                      </div>
                    </div>
                    <div style={styles.constraintSummary}>
                      <h4 style={styles.constraintTitle}>
                        Gene Constraint Summary
                      </h4>
                      <div style={styles.constraintGrid}>
                        <div style={styles.constraintItem}>
                          <span style={styles.constraintLabel}>pLI</span>
                          <span
                            style={{
                              ...styles.constraintValue,
                              ...getPliColor(data.lofScores?.pLI ?? null),
                            }}
                          >
                            {data.lofScores?.pLI?.toFixed(2) ?? 'N/A'}
                          </span>
                        </div>
                        <div style={styles.constraintItem}>
                          <span style={styles.constraintLabel}>LOEUF</span>
                          <span
                            style={{
                              ...styles.constraintValue,
                              ...getLoeufColor(data.lofScores?.loeuf ?? null),
                            }}
                          >
                            {data.lofScores?.loeuf?.toFixed(2) ?? 'N/A'}
                          </span>
                        </div>
                        <div style={styles.constraintItem}>
                          <span style={styles.constraintLabel}>
                            LoF Count (gnomAD)
                          </span>
                          <span style={styles.constraintValue}>
                            {data.lofScores?.lofHcCount ?? 'N/A'}
                          </span>
                        </div>
                      </div>
                      {data.lofScores?.pLI != null && data.lofScores.pLI > 0.9 && (
                        <div style={styles.constraintWarning}>
                          <strong>Note:</strong> This gene is highly intolerant
                          to loss-of-function variants (pLI {'>'} 0.9). LoF
                          variants in this gene are more likely to be
                          pathogenic.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Other variant types
                  <div style={styles.genericAssessment}>
                    <p>
                      Standard pathogenicity predictions (AlphaMissense, SIFT,
                      PolyPhen) are not applicable for{' '}
                      {data.variantType.toLowerCase()} variants.
                    </p>
                    <p>
                      <strong>Mechanism:</strong> {data.molecularMechanism}
                    </p>
                  </div>
                )}
              </div>

              {/* Molecular Details Card */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🔬</span>
                  Molecular Details
                </h3>
                <div style={styles.detailsGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Variant Type</span>
                    <span
                      style={{
                        ...styles.detailValue,
                        ...styles.typeBadge,
                        backgroundColor: data.isLofVariant
                          ? '#fef2f2'
                          : '#f0fdf4',
                        color: data.isLofVariant ? '#dc2626' : '#166534',
                      }}
                    >
                      {data.variantType}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Mechanism</span>
                    <span style={styles.detailValue}>
                      {data.molecularMechanism}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Impact</span>
                    <span
                      style={{
                        ...styles.detailValue,
                        ...styles.impactBadge,
                        backgroundColor:
                          data.structuralImpact === 'HIGH'
                            ? '#fef2f2'
                            : data.structuralImpact === 'MODERATE'
                            ? '#fefce8'
                            : '#f0fdf4',
                        color:
                          data.structuralImpact === 'HIGH'
                            ? '#dc2626'
                            : data.structuralImpact === 'MODERATE'
                            ? '#ca8a04'
                            : '#16a34a',
                      }}
                    >
                      {data.structuralImpact}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Amino Acid Change</span>
                    <span style={styles.detailValue}>
                      {data.aminoAcids || 'N/A'}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Codon Change</span>
                    <span style={styles.detailValueMono}>
                      {data.codons || 'N/A'}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>HGVS Coding</span>
                    <span style={styles.detailValueMono}>{data.hgvsGenomic}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Transcripts Affected</span>
                    <span style={styles.detailValue}>
                      {data.transcriptCount} protein-coding
                    </span>
                  </div>
                </div>
              </div>

              {/* ===========================================
                  NEW: Transcript & Protein Information Card (moved from Details tab)
                  =========================================== */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>📝</span>
                  Transcript & Protein Information
                </h3>
                <div style={styles.transcriptDetails}>
                  <div style={styles.transcriptItem}>
                    <span style={styles.transcriptLabel}>
                      Canonical Transcript
                    </span>
                    {data.canonicalTranscript ? (
                      <a
                        href={`https://www.ensembl.org/Homo_sapiens/Transcript/Summary?t=${data.canonicalTranscript}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.transcriptLink}
                      >
                        {data.canonicalTranscript}
                      </a>
                    ) : (
                      <span style={styles.transcriptValue}>N/A</span>
                    )}
                  </div>
                  <div style={styles.transcriptItem}>
                    <span style={styles.transcriptLabel}>Protein ID</span>
                    {data.proteinId ? (
                      <a
                        href={`https://www.uniprot.org/uniprotkb?query=${data.proteinId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.transcriptLink}
                      >
                        {data.proteinId}
                      </a>
                    ) : (
                      <span style={styles.transcriptValue}>N/A</span>
                    )}
                  </div>
                  <div style={styles.transcriptItem}>
                    <span style={styles.transcriptLabel}>Genomic Position</span>
                    <span style={styles.transcriptValueMono}>
                      {data.genomicCoordinates}
                    </span>
                  </div>
                  <div style={styles.transcriptItem}>
                    <span style={styles.transcriptLabel}>Protein Change</span>
                    <span style={styles.transcriptValueMono}>
                      {data.proteinChange}
                    </span>
                  </div>
                </div>
              </div>

              {/* Population Frequency Summary Card */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🌍</span>
                  Population Frequencies
                </h3>
                {data.populationFrequencies ? (
                  <div style={styles.frequencyContent}>
                    <div style={styles.globalFreqSection}>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>Global AF</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(data.populationFrequencies.globalAF)}
                        </span>
                      </div>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>gnomAD Genomes</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(data.populationFrequencies.gnomadGenomes)}
                        </span>
                      </div>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>gnomAD Exomes</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(data.populationFrequencies.gnomadExomes)}
                        </span>
                      </div>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>Max Population AF</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(getMaxPopulationFrequency().maxFreq)}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
                      Full population breakdown is available in the Population tab.
                    </div>
                  </div>
                ) : (
                  <p style={styles.emptyText}>Population frequency data not available.</p>
                )}
              </div>

              {/* ===========================================
    NEW: Quick Links Card (moved from Details tab) - FULL WIDTH
    =========================================== */}
<div style={{ ...styles.card, gridColumn: '1 / -1' }}>
  <h3 style={styles.cardTitle}>
    <span style={styles.cardIcon}>🔗</span>
    Quick Links
  </h3>
  <div style={styles.quickLinks}>
    <a
      href={`https://www.ncbi.nlm.nih.gov/clinvar/?term=${data.gene}[gene]+AND+${data.proteinChange}`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>🏥</span>
      Search ClinVar
    </a>
    <a
      href={`https://gnomad.broadinstitute.org/gene/${data.gene}?dataset=gnomad_r4`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>👥</span>
      View in gnomAD
    </a>
    <a
      href={`https://www.uniprot.org/uniprotkb?query=${data.gene}+AND+organism_id:9606`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>🧬</span>
      UniProt Entry
    </a>
    <a
      href={`https://www.genecards.org/cgi-bin/carddisp.pl?gene=${data.gene}`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>📚</span>
      GeneCards
    </a>
    <a
      href={`https://pubmed.ncbi.nlm.nih.gov/?term=${data.gene}+${data.proteinChange}`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>📄</span>
      Search PubMed
    </a>
    <a
      href={`https://cancer.sanger.ac.uk/cosmic/gene/analysis?ln=${data.gene}`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>🔬</span>
      COSMIC Gene
    </a>
    {data.isSpliceVariant && (
      <a
        href="https://spliceailookup.broadinstitute.org/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...styles.quickLink,
          backgroundColor: '#f0fdf4',
          borderColor: '#bbf7d0',
        }}
      >
        <span style={styles.quickLinkIcon}>🧬</span>
        SpliceAI Lookup
      </a>
    )}
    <a
      href={`https://www.ensembl.org/Homo_sapiens/Gene/Summary?g=${data.gene}`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>🧫</span>
      Ensembl Gene
    </a>
    <a
      href={`https://www.ncbi.nlm.nih.gov/gene/?term=${data.gene}[gene]+AND+human[organism]`}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.quickLink}
    >
      <span style={styles.quickLinkIcon}>🏛️</span>
      NCBI Gene
    </a>
  </div>
</div>

              {/* AlphaGenome Prediction Card */}
              <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🧠</span>
                  AlphaGenome Analysis
                  {data.isLofVariant && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        padding: '2px 8px',
                        borderRadius: '10px',
                      }}
                    >
                      LoF Context
                    </span>
                  )}
                </h3>
                <p style={styles.alphaGenomeText}>{data.alphaGenomePrediction}</p>
                {data.alphaGenomeData && (
                  <div style={styles.alphaGenomeDetails}>
                    <span style={styles.alphaGenomeItem}>
                      <strong>Source:</strong> {data.alphaGenomeData.source}
                    </span>
                    {data.alphaGenomeData.tissue_type && (
                      <span style={styles.alphaGenomeItem}>
                        <strong>Tissue:</strong>{' '}
                        {data.alphaGenomeData.tissue_type}
                      </span>
                    )}
                    {data.alphaGenomeData.interval && (
                      <span style={styles.alphaGenomeItem}>
                        <strong>Region:</strong>{' '}
                        {data.alphaGenomeData.interval.chromosome}:
                        {data.alphaGenomeData.interval.start.toLocaleString()}-
                        {data.alphaGenomeData.interval.end.toLocaleString()}
                      </span>
                    )}
                    {data.alphaGenomeData.expression_change_percent !==
                      undefined &&
                      data.alphaGenomeData.expression_change_percent !== null && (
                        <span
                          style={{
                            ...styles.alphaGenomeItem,
                            color:
                              Math.abs(
                                data.alphaGenomeData.expression_change_percent
                              ) > 10
                                ? '#dc2626'
                                : '#64748b',
                            fontWeight:
                              Math.abs(
                                data.alphaGenomeData.expression_change_percent
                              ) > 10
                                ? 600
                                : 400,
                          }}
                        >
                          <strong>Expression Δ:</strong>{' '}
                          {data.alphaGenomeData.expression_change_percent > 0
                            ? '+'
                            : ''}
                          {data.alphaGenomeData.expression_change_percent.toFixed(
                            1
                          )}
                          %
                        </span>
                      )}
                  </div>
                )}

                {/* Note for LoF variants about AlphaMissense */}
                {data.isLofVariant && data.alphaMissenseScore === -1 && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #bae6fd',
                      fontSize: '13px',
                      color: '#0369a1',
                    }}
                  >
                    <strong>Note:</strong> AlphaMissense scores are designed for
                    missense variants only. For this{' '}
                    {data.variantType.toLowerCase()} variant, gene constraint
                    metrics (pLI/LOEUF) and NMD predictions above provide the
                    pathogenicity assessment.
                  </div>
                )}

                {/* Splicing Predictions for splice variants */}
                {data.isSpliceVariant && data.splicePredictions && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '10px',
                      border: '1px solid #bbf7d0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '14px',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>🧬</span>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: '#166534' }}>
                        Splicing Predictions
                      </span>
                    </div>

                    <div style={styles.predictionGrid}>
                      {/* SpliceAI */}
                      {data.splicePredictions.spliceAI_maxDelta !== null && (
                        <div style={styles.predictionItem}>
                          <div style={styles.predictionHeader}>
                            <span style={styles.predictionName}>SpliceAI</span>
                            <span style={styles.predictionBadge}>Deep Learning</span>
                          </div>
                          <div style={styles.predictionScore}>
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 700,
                                color:
                                  data.splicePredictions.spliceAI_maxDelta >= 0.8
                                    ? '#dc2626'
                                    : data.splicePredictions.spliceAI_maxDelta >= 0.5
                                    ? '#ca8a04'
                                    : data.splicePredictions.spliceAI_maxDelta >= 0.2
                                    ? '#16a34a'
                                    : '#9ca3af',
                              }}
                            >
                              {data.splicePredictions.spliceAI_maxDelta.toFixed(2)}
                            </span>
                            <span style={styles.predictionClass}>
                              {data.splicePredictions.spliceAI_maxDelta >= 0.8
                                ? 'High Impact'
                                : data.splicePredictions.spliceAI_maxDelta >= 0.5
                                ? 'Moderate Impact'
                                : data.splicePredictions.spliceAI_maxDelta >= 0.2
                                ? 'Low Impact'
                                : 'Minimal'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
                              <span>AG: {data.splicePredictions.spliceAI_DS_AG?.toFixed(2) ?? 'N/A'}</span>
                              <span>AL: {data.splicePredictions.spliceAI_DS_AL?.toFixed(2) ?? 'N/A'}</span>
                              <span>DG: {data.splicePredictions.spliceAI_DS_DG?.toFixed(2) ?? 'N/A'}</span>
                              <span>DL: {data.splicePredictions.spliceAI_DS_DL?.toFixed(2) ?? 'N/A'}</span>
                            </div>
                          </div>
                          <div style={styles.predictionNote}>
                            Max delta score. {'>'}=0.8 = High, {'>'}=0.5 = Moderate, {'>'}=0.2 = Low
                          </div>
                        </div>
                      )}

                      {/* MaxEntScan */}
                      {data.splicePredictions.maxEntScan_ref !== null && (
                        <div style={styles.predictionItem}>
                          <div style={styles.predictionHeader}>
                            <span style={styles.predictionName}>MaxEntScan</span>
                            <span style={styles.predictionBadge}>Splice Strength</span>
                          </div>
                          <div style={styles.predictionScore}>
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 700,
                                color:
                                  data.splicePredictions.maxEntScan_diff !== null && data.splicePredictions.maxEntScan_diff < -3
                                    ? '#dc2626'
                                    : data.splicePredictions.maxEntScan_diff !== null && data.splicePredictions.maxEntScan_diff < 0
                                    ? '#ca8a04'
                                    : '#16a34a',
                              }}
                            >
                              {data.splicePredictions.maxEntScan_diff !== null
                                ? `${data.splicePredictions.maxEntScan_diff >= 0 ? '+' : ''}${data.splicePredictions.maxEntScan_diff.toFixed(2)}`
                                : 'N/A'}
                            </span>
                            <span style={styles.predictionClass}>
                              {data.splicePredictions.maxEntScan_diff !== null && data.splicePredictions.maxEntScan_diff < -3
                                ? 'Strongly Weakened'
                                : data.splicePredictions.maxEntScan_diff !== null && data.splicePredictions.maxEntScan_diff < 0
                                ? 'Weakened'
                                : 'Maintained/Strengthened'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                            Ref: {data.splicePredictions.maxEntScan_ref.toFixed(2)} | Alt: {data.splicePredictions.maxEntScan_alt?.toFixed(2) ?? 'N/A'}
                          </div>
                          <div style={styles.predictionNote}>
                            Diff = alt − ref (negative = weakened splice site)
                          </div>
                        </div>
                      )}

                      {/* dbscSNV */}
                      {(data.splicePredictions.dbscSNV_ada !== null || data.splicePredictions.dbscSNV_rf !== null) && (
                        <div style={styles.predictionItem}>
                          <div style={styles.predictionHeader}>
                            <span style={styles.predictionName}>dbscSNV</span>
                            <span style={styles.predictionBadge}>Consensus</span>
                          </div>
                          <div style={styles.predictionScore}>
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 700,
                                color:
                                  (data.splicePredictions.dbscSNV_ada !== null && data.splicePredictions.dbscSNV_ada >= 0.6) ||
                                  (data.splicePredictions.dbscSNV_rf !== null && data.splicePredictions.dbscSNV_rf >= 0.6)
                                    ? '#dc2626'
                                    : '#16a34a',
                              }}
                            >
                              {data.splicePredictions.dbscSNV_ada?.toFixed(3) ?? data.splicePredictions.dbscSNV_rf?.toFixed(3) ?? 'N/A'}
                            </span>
                            <span style={styles.predictionClass}>
                              {(data.splicePredictions.dbscSNV_ada !== null && data.splicePredictions.dbscSNV_ada >= 0.6) ||
                               (data.splicePredictions.dbscSNV_rf !== null && data.splicePredictions.dbscSNV_rf >= 0.6)
                                ? 'Splice-Affecting'
                                : 'Not Splice-Affecting'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                            ADA: {data.splicePredictions.dbscSNV_ada?.toFixed(3) ?? 'N/A'} | RF: {data.splicePredictions.dbscSNV_rf?.toFixed(3) ?? 'N/A'}
                          </div>
                          <div style={styles.predictionNote}>
                            Score {'>'}= 0.6 suggests splice-affecting
                          </div>
                        </div>
                      )}

                      {/* EVE */}
                      {data.splicePredictions.eveScore !== null && (
                        <div style={styles.predictionItem}>
                          <div style={styles.predictionHeader}>
                            <span style={styles.predictionName}>EVE</span>
                            <span style={styles.predictionBadge}>Evolutionary</span>
                          </div>
                          <div style={styles.predictionScore}>
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 700,
                                color:
                                  data.splicePredictions.eveScore >= 0.75
                                    ? '#dc2626'
                                    : data.splicePredictions.eveScore <= 0.3
                                    ? '#16a34a'
                                    : '#ca8a04',
                              }}
                            >
                              {data.splicePredictions.eveScore.toFixed(3)}
                            </span>
                            <span style={styles.predictionClass}>
                              {data.splicePredictions.eveClass
                                ? data.splicePredictions.eveClass.replace(/_/g, ' ')
                                : data.splicePredictions.eveScore >= 0.75
                                ? 'Pathogenic'
                                : data.splicePredictions.eveScore <= 0.3
                                ? 'Benign'
                                : 'Uncertain'}
                            </span>
                          </div>
                          <div style={styles.predictionNote}>
                            {'>'}=0.75 = Pathogenic, {'<'}=0.3 = Benign
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Splice Consequence Terms */}
                    {data.splicePredictions.spliceConsequences.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#166534', marginBottom: '6px' }}>
                          Transcript Consequences
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {data.splicePredictions.spliceConsequences.map((term, i) => (
                            <span
                              key={i}
                              style={{
                                padding: '3px 8px',
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 500,
                                fontFamily: 'monospace',
                              }}
                            >
                              {term.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fallback: link to SpliceAI Lookup if no SpliceAI data */}
                    {data.splicePredictions.spliceAI_maxDelta === null && (
                      <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#14532d', lineHeight: 1.5 }}>
                        SpliceAI scores were not available from Ensembl for this variant. You can check{' '}
                        <a
                          href="https://spliceailookup.broadinstitute.org/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#15803d', fontWeight: 500 }}
                        >
                          SpliceAI Lookup
                        </a>
                        {' '}for additional predictions.
                      </p>
                    )}
                  </div>
                )}
                {/* Fallback: no splice predictions data at all */}
                {data.isSpliceVariant && !data.splicePredictions && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      border: '1px solid #bbf7d0',
                      fontSize: '13px',
                      color: '#14532d',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>🧬</span>
                      <span style={{ fontWeight: 600, color: '#166534' }}>
                        Splice Variant Analysis
                      </span>
                    </div>
                    <p style={{ margin: 0, lineHeight: 1.5 }}>
                      Splice prediction scores (SpliceAI, MaxEntScan, dbscSNV, EVE) were not
                      available from Ensembl VEP for this variant. Consider using{' '}
                      <a
                        href="https://spliceailookup.broadinstitute.org/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#15803d', fontWeight: 500 }}
                      >
                        SpliceAI Lookup
                      </a>
                      {' '}for detailed splice effect predictions.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===========================================
              Clinical Tab
              =========================================== */}
          {activeTab === 'clinical' && (
            <div style={styles.clinicalGrid}>
              {/* ClinVar Data */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🏥</span>
                  ClinVar Classification
                </h3>
                {data.clinvarData ? (
                  <div style={styles.clinvarContent}>
                    <div style={styles.clinvarSignificance}>
                      {data.clinvarData.clinicalSignificance.map((sig, idx) => (
                        <span
                          key={idx}
                          style={{
                            ...styles.significanceBadge,
                            backgroundColor: sig.includes('pathogenic')
                              ? '#fef2f2'
                              : sig.includes('benign')
                              ? '#f0fdf4'
                              : '#f9fafb',
                            color: sig.includes('pathogenic')
                              ? '#dc2626'
                              : sig.includes('benign')
                              ? '#16a34a'
                              : '#6b7280',
                            borderColor: sig.includes('pathogenic')
                              ? '#fecaca'
                              : sig.includes('benign')
                              ? '#bbf7d0'
                              : '#e5e7eb',
                          }}
                        >
                          {sig.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                    {data.clinvarData.conditions.length > 0 && (
                      <div style={styles.conditionsSection}>
                        <h4 style={styles.subSectionTitle}>
                          Associated Conditions
                        </h4>
                        <ul style={styles.conditionsList}>
                          {data.clinvarData.conditions.map((condition, idx) => (
                            <li key={idx} style={styles.conditionItem}>
                              {condition}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {data.clinvarData.somaticClassification && (
                      <div style={styles.somaticSection}>
                        <h4 style={styles.subSectionTitle}>
                          Somatic Classification
                        </h4>
                        <p style={styles.somaticText}>
                          {data.clinvarData.somaticClassification}
                        </p>
                      </div>
                    )}
                    {data.clinvarData.variantIds.length > 0 && (
                      <div style={styles.variantIdsSection}>
                        <h4 style={styles.subSectionTitle}>ClinVar IDs</h4>
                        <div style={styles.idList}>
                          {data.clinvarData.variantIds.map((id, idx) => (
                            <a
                              key={idx}
                              href={`https://www.ncbi.nlm.nih.gov/clinvar/${id}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.externalLink}
                            >
                              {id}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={styles.noData}>
                    No ClinVar data available for this variant.
                  </p>
                )}
              </div>

              {/* Reported Clinical Findings */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>📋</span>
                  Literature Clinical Findings
                </h3>
                {data.reportedClinicalFindings ? (
                  <div style={styles.clinvarContent}>
                    <p style={styles.somaticText}>
                      {renderLinkedSummary(data.reportedClinicalFindings.summary)}
                    </p>
                    {data.reportedClinicalFindings.findings.length > 0 ? (
                      <div style={styles.conditionsSection}>
                        <div style={styles.idList}>
                          <span style={styles.pubmedCount}>
                            {data.reportedClinicalFindings.exactVariantEvidenceCount} exact variant literature
                          </span>
                        </div>
                        <div style={styles.conditionsList}>
                          {data.reportedClinicalFindings.findings.slice(0, 10).map((finding, idx) => (
                            <div key={`${finding.source}-${finding.pmid || finding.accession || idx}`} style={styles.conditionItem}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                                <span style={styles.hgmdId}>{finding.source}</span>
                                <span style={styles.moreCount}>
                                  {finding.matchConfidence.replace(/_/g, ' ')}
                                </span>
                                {finding.condition && (
                                  <span style={styles.pubmedCount}>{finding.condition}</span>
                                )}
                              </div>
                              {finding.url && (
                                <a
                                  href={finding.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={styles.externalLink}
                                >
                                  {finding.pmid ? `PMID:${finding.pmid}` : finding.accession || 'Source'}
                                </a>
                              )}
                            </div>
                          ))}
                          {data.reportedClinicalFindings.findings.length > 10 && (
                            <span style={styles.moreCount}>
                              +{data.reportedClinicalFindings.findings.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span style={styles.noDataSmall}>No literature rows found</span>
                    )}
                  </div>
                ) : (
                  <p style={styles.noData}>
                    No literature clinical findings were collected for this variant.
                  </p>
                )}
              </div>

              {/* External Databases */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🔗</span>
                  External Databases
                </h3>
                <div style={styles.externalDbGrid}>
                  {/* COSMIC */}
                  <div style={styles.dbSection}>
                    <h4 style={styles.dbTitle}>
                      <span style={styles.dbIcon}>🔬</span>
                      COSMIC
                    </h4>
                    {data.cosmicIds && data.cosmicIds.length > 0 ? (
                      <div style={styles.idList}>
                        {data.cosmicIds.slice(0, 5).map((id, idx) => (
                          <a
                            key={idx}
                            href={`https://cancer.sanger.ac.uk/cosmic/search?q=${id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.externalLink}
                          >
                            {id}
                          </a>
                        ))}
                        {data.cosmicIds.length > 5 && (
                          <span style={styles.moreCount}>
                            +{data.cosmicIds.length - 5} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={styles.noDataSmall}>Not found in COSMIC</span>
                    )}
                  </div>

                  {/* HGMD */}
                  <div style={styles.dbSection}>
                    <h4 style={styles.dbTitle}>
                      <span style={styles.dbIcon}>📚</span>
                      HGMD
                    </h4>
                    {data.hgmdIds && data.hgmdIds.length > 0 ? (
                      <div style={styles.idList}>
                        {data.hgmdIds.map((id, idx) => (
                          <span key={idx} style={styles.hgmdId}>
                            {id}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={styles.noDataSmall}>Not found in HGMD</span>
                    )}
                  </div>

                  {/* PubMed */}
                  <div style={styles.dbSection}>
                    <h4 style={styles.dbTitle}>
                      <span style={styles.dbIcon}>📄</span>
                      PubMed References
                    </h4>
                    {data.pubmedIds && data.pubmedIds.length > 0 ? (
                      <div style={styles.pubmedSection}>
                        <span style={styles.pubmedCount}>
                          {data.pubmedIds.length} publication
                          {data.pubmedIds.length > 1 ? 's' : ''}
                        </span>
                        <div style={styles.idList}>
                          {data.pubmedIds.slice(0, 3).map((id, idx) => (
                            <a
                              key={idx}
                              href={`https://pubmed.ncbi.nlm.nih.gov/${id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.externalLink}
                            >
                              PMID:{id}
                            </a>
                          ))}
                          {data.pubmedIds.length > 3 && (
                            <span style={styles.moreCount}>
                              +{data.pubmedIds.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span style={styles.noDataSmall}>No PubMed references</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===========================================
              Population Tab
              =========================================== */}
          {activeTab === 'population' && (
            <div style={styles.populationGrid}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>🌍</span>
                  Population Frequencies (gnomAD)
                </h3>
                {data.populationFrequencies ? (
                  <div style={styles.frequencyContent}>
                    {/* Global Frequencies */}
                    <div style={styles.globalFreqSection}>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>
                          Global Allele Frequency
                        </span>
                        <span style={styles.freqValue}>
                          {formatFrequency(data.populationFrequencies.globalAF)}
                        </span>
                      </div>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>gnomAD Genomes</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(
                            data.populationFrequencies.gnomadGenomes
                          )}
                        </span>
                      </div>
                      <div style={styles.globalFreqItem}>
                        <span style={styles.freqLabel}>gnomAD Exomes</span>
                        <span style={styles.freqValue}>
                          {formatFrequency(
                            data.populationFrequencies.gnomadExomes
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Population Breakdown */}
                    <h4 style={styles.subSectionTitle}>Population Breakdown</h4>
                    <div style={styles.populationBars}>
                      {[
                        {
                          name: 'African/African American',
                          key: 'afr',
                          color: '#8b5cf6',
                        },
                        {
                          name: 'American (Latino)',
                          key: 'amr',
                          color: '#ec4899',
                        },
                        {
                          name: 'Ashkenazi Jewish',
                          key: 'asj',
                          color: '#f59e0b',
                        },
                        { name: 'East Asian', key: 'eas', color: '#10b981' },
                        { name: 'Finnish', key: 'fin', color: '#3b82f6' },
                        {
                          name: 'Non-Finnish European',
                          key: 'nfe',
                          color: '#6366f1',
                        },
                        { name: 'South Asian', key: 'sas', color: '#ef4444' },
                      ].map((pop) => {
                        const freq =
                          data.populationFrequencies?.gnomadPopulations?.[
                            pop.key as keyof typeof data.populationFrequencies.gnomadPopulations
                          ];
                        const freqPercent = freq ? freq * 100 : 0;
                        const maxPercent = 1;
                        const barWidth = Math.min(
                          (freqPercent / maxPercent) * 100,
                          100
                        );

                        return (
                          <div key={pop.key} style={styles.populationBar}>
                            <div style={styles.popBarHeader}>
                              <span style={styles.popName}>{pop.name}</span>
                              <span style={styles.popFreq}>
                                {formatFrequency(freq)}
                              </span>
                            </div>
                            <div style={styles.popBarTrack}>
                              <div
                                style={{
                                  ...styles.popBarFill,
                                  width: `${barWidth}%`,
                                  backgroundColor: pop.color,
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Interpretation */}
                    <div style={styles.freqInterpretation}>
                      <span style={styles.freqInterpretIcon}>💡</span>
                      <span>{getFrequencyInterpretation()}</span>
                    </div>
                  </div>
                ) : (
                  <p style={styles.noData}>
                    No population frequency data available for this variant.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ===========================================
              Structure Tab
              =========================================== */}
          {activeTab === 'structure' && (
            <div style={styles.structureGrid}>
              <div style={{ ...styles.card, gridColumn: '1 / -1', overflow: 'hidden', width: '100%', boxSizing: 'border-box' as const, padding: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px', marginBottom: '16px' }}>
                  <h3 style={{ ...styles.cardTitle, margin: 0, fontSize: '15px' }}>
                    <span style={styles.cardIcon}>🧬</span>
                    3D Protein Structure
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        backgroundColor: '#dbeafe',
                        color: '#1d4ed8',
                        padding: '2px 8px',
                        borderRadius: '10px',
                      }}
                    >
                      AlphaFold
                    </span>
                  </h3>
                  
                  {/* Structure View Toggle */}
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' as const }}>
                    <button
                      onClick={() => setStructureView('classic')}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: structureView === 'classic' ? '#3b82f6' : 'white',
                        color: structureView === 'classic' ? 'white' : '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      Classic View
                    </button>
                    <button
                      onClick={() => setStructureView('galaxy')}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: structureView === 'galaxy' ? '#3b82f6' : 'white',
                        color: structureView === 'galaxy' ? 'white' : '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      🌌 Variant Galaxy
                    </button>
                  </div>
                </div>

                {/* Structure Viewer */}
                {structureView === 'classic' ? (
                  <ProteinStructureViewer
                    geneSymbol={data.gene}
                    proteinChange={data.proteinChange}
                    uniprotId={
                      data.proteinId?.startsWith('ENSP') ? null : data.proteinId
                    }
                    height={450}
                  />
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <VariantGalaxy variantData={data} />
                  </div>
                )}

                {/* Structure Context for LoF variants */}
                {data.isLofVariant && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px',
                      border: '1px solid #fde68a',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>⚠️</span>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#92400e',
                        }}
                      >
                        Loss-of-Function Variant
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#78350f',
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      This {data.variantType.toLowerCase()} variant is predicted
                      to cause loss of protein function.
                      {data.isNonsenseVariant &&
                        ' The structure shown is the wild-type protein; the premature stop codon would truncate the protein at this position.'}
                      {data.isFrameshiftVariant &&
                        ' The frameshift would disrupt the protein sequence downstream of the indicated position.'}
                      {data.isSpliceVariant &&
                        ' Splice variants may result in exon skipping or intron retention, altering the protein structure.'}
                    </p>
                  </div>
                )}

                {/* Missense context */}
                {data.isMissenseVariant && data.alphaMissenseScore !== -1 && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '12px 16px',
                      backgroundColor:
                        data.alphaMissenseScore >= 0.564
                          ? '#fef2f2'
                          : data.alphaMissenseScore <= 0.34
                          ? '#f0fdf4'
                          : '#fefce8',
                      borderRadius: '8px',
                      border: `1px solid ${
                        data.alphaMissenseScore >= 0.564
                          ? '#fecaca'
                          : data.alphaMissenseScore <= 0.34
                          ? '#bbf7d0'
                          : '#fef08a'
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>🎯</span>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color:
                            data.alphaMissenseScore >= 0.564
                              ? '#991b1b'
                              : data.alphaMissenseScore <= 0.34
                              ? '#166534'
                              : '#854d0e',
                        }}
                      >
                        Structural Impact Assessment
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: '13px',
                        color:
                          data.alphaMissenseScore >= 0.564
                            ? '#7f1d1d'
                            : data.alphaMissenseScore <= 0.34
                            ? '#14532d'
                            : '#78350f',
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      The amino acid change at position{' '}
                      {getVariantPosition() || 'unknown'} has an AlphaMissense
                      pathogenicity score of{' '}
                      <strong>{data.alphaMissenseScore.toFixed(3)}</strong> (
                      {data.alphaMissenseClass}).
                      {data.alphaMissenseScore >= 0.564 &&
                        ' This substitution likely affects protein structure or function.'}
                      {data.alphaMissenseScore <= 0.34 &&
                        ' This substitution is likely tolerated by the protein structure.'}
                      {data.alphaMissenseScore > 0.34 &&
                        data.alphaMissenseScore < 0.564 &&
                        ' The impact of this substitution on protein structure is uncertain.'}
                    </p>
                  </div>
                )}

                {/* Structural domains info */}
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>ℹ️</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#0369a1',
                      }}
                    >
                      About This Structure
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: '13px',
                      color: '#0c4a6e',
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    This structure is predicted by{' '}
                    <a
                      href="https://alphafold.ebi.ac.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0284c7', fontWeight: 500 }}
                    >
                      AlphaFold2
                    </a>
                    . Colors indicate prediction confidence (pLDDT score): blue
                    regions are high confidence, while orange/yellow regions have
                    lower confidence and may be flexible or disordered. The
                    variant position is highlighted in red.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===========================================
// Styles
// ===========================================
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  // Header styles
  header: {
    background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
    padding: '16px',
    color: 'white',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  geneInfo: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    flexWrap: 'wrap',
  },
  geneName: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  proteinChange: {
    fontSize: '18px',
    fontWeight: 500,
    opacity: 0.9,
  },
  variantTypeBadge: {
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '4px 10px',
    borderRadius: '12px',
  },
  classificationBadge: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    border: '2px solid',
    whiteSpace: 'nowrap',
  },
  headerMeta: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    opacity: 0.9,
    wordBreak: 'break-all' as const,
    minWidth: 0,
  },
  metaIcon: {
    fontSize: '14px',
  },
  // Missense Score Card
  scoreCard: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  scoreCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  scoreCardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
  },
  scoreSource: {
    fontSize: '12px',
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    padding: '4px 10px',
    borderRadius: '12px',
  },
  scoreDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
  },
  scoreValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '80px',
  },
  scoreNumber: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1,
  },
  scoreLabel: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '8px',
  },
  scoreBarContainer: {
    flex: 1,
    minWidth: '0',
  },
  scoreBar: {
    position: 'relative',
    height: '12px',
    backgroundColor: '#e2e8f0',
    borderRadius: '6px',
    overflow: 'visible',
  },
  scoreBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.5s ease-out',
  },
  scoreBarMarker: {
    position: 'absolute',
    top: '-4px',
    width: '2px',
    height: '20px',
    backgroundColor: '#94a3b8',
    borderRadius: '1px',
  },
  scoreBarLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '11px',
    color: '#64748b',
  },
  // LoF Score Card
  lofScoreCard: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    borderBottom: '1px solid #fecaca',
  },
  lofBadge: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#dc2626',
    backgroundColor: '#fee2e2',
    padding: '4px 12px',
    borderRadius: '12px',
    border: '1px solid #fecaca',
  },
  lofScoreDisplay: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  lofScoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
    gap: '20px',
  },
  lofScoreItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #fecaca',
  },
  lofScoreLabel: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '4px',
  },
  lofScoreValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#dc2626',
  },
  lofScoreHint: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  lofMechanismText: {
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  nmdSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #fecaca',
  },
  nmdHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  nmdTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  nmdBadge: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '12px',
  },
  nmdExplanation: {
    fontSize: '13px',
    color: '#64748b',
    lineHeight: 1.5,
    margin: 0,
  },
  genericInfo: {
    fontSize: '14px',
    color: '#475569',
    lineHeight: 1.6,
  },
  // Tab styles
  tabContainer: {
    padding: '0',
  },
  tabNav: {
    display: 'flex',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 8px',
    backgroundColor: '#f8fafc',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  tabButton: {
    padding: '12px 10px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '-1px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  tabButtonActive: {
    color: '#4f46e5',
    borderBottomColor: '#4f46e5',
    backgroundColor: '#ffffff',
  },
  tabContent: {
    padding: '16px',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
  },
  // Grid layouts
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    gap: '20px',
  },
  clinicalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    gap: '20px',
  },
  populationGrid: {
    display: 'grid',
    gap: '20px',
  },
  structureGrid: {
    display: 'grid',
    gap: '20px',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
  },
  // Card styles
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    boxSizing: 'border-box' as const,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    margin: '0 0 16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardIcon: {
    fontSize: '18px',
  },
  // Prediction styles
  predictionGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  predictionItem: {
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
  },
  predictionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  predictionName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#334155',
  },
  predictionBadge: {
    fontSize: '10px',
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  predictionScore: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  predictionClass: {
    fontSize: '13px',
    color: '#64748b',
    textTransform: 'capitalize',
  },
  predictionNote: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '6px',
    fontStyle: 'italic',
  },
  // LoF Assessment styles
  lofAssessment: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  assessmentItem: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  },
  assessmentIcon: {
    fontSize: '24px',
  },
  assessmentContent: {
    flex: 1,
  },
  assessmentTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#991b1b',
    margin: '0 0 8px 0',
  },
  assessmentText: {
    fontSize: '13px',
    color: '#7f1d1d',
    margin: 0,
    lineHeight: 1.5,
  },
  constraintSummary: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  constraintTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#334155',
    margin: '0 0 12px 0',
  },
  constraintGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(80px, 100%), 1fr))',
    gap: '12px',
    marginBottom: '12px',
  },
  constraintItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  constraintLabel: {
    fontSize: '11px',
    color: '#64748b',
  },
  constraintValue: {
    fontSize: '18px',
    fontWeight: 700,
  },
  constraintWarning: {
    fontSize: '12px',
    color: '#b91c1c',
    backgroundColor: '#fee2e2',
    padding: '10px 12px',
    borderRadius: '6px',
    lineHeight: 1.4,
  },
  genericAssessment: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#475569',
    lineHeight: 1.6,
  },
  // Details styles
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))',
    gap: '12px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 500,
  },
  detailValue: {
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: 500,
  },
  detailValueMono: {
    fontSize: '13px',
    color: '#1e293b',
    fontFamily: 'monospace',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    wordBreak: 'break-all',
  },
  typeBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    width: 'fit-content',
  },
  impactBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    width: 'fit-content',
  },
  interpretation: {
    fontSize: '15px',
    lineHeight: 1.7,
    color: '#334155',
    margin: 0,
  },
  alphaGenomeText: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#334155',
    margin: '0 0 12px 0',
  },
  alphaGenomeDetails: {
    display: 'flex',
    gap: '20px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    fontSize: '13px',
    flexWrap: 'wrap',
  },
  alphaGenomeItem: {
    color: '#64748b',
  },
  // Clinical styles
  clinvarContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  clinvarSignificance: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  significanceBadge: {
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid',
    textTransform: 'capitalize',
  },
  conditionsSection: {
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  subSectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569',
    margin: '0 0 10px 0',
  },
  conditionsList: {
    margin: 0,
    paddingLeft: '20px',
  },
  conditionItem: {
    fontSize: '14px',
    color: '#334155',
    marginBottom: '6px',
  },
  somaticSection: {
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  somaticText: {
    fontSize: '13px',
    color: '#475569',
    margin: 0,
    lineHeight: 1.5,
  },
  variantIdsSection: {
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  noData: {
    color: '#94a3b8',
    fontSize: '14px',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px',
  },
  noDataSmall: {
    color: '#94a3b8',
    fontSize: '13px',
    fontStyle: 'italic',
  },
  externalDbGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  dbSection: {
    paddingBottom: '16px',
    borderBottom: '1px solid #f1f5f9',
  },
  dbTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#334155',
    margin: '0 0 10px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dbIcon: {
    fontSize: '14px',
  },
  idList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  externalLink: {
    fontSize: '13px',
    color: '#4f46e5',
    textDecoration: 'none',
    backgroundColor: '#eef2ff',
    padding: '4px 10px',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
  },
  hgmdId: {
    fontSize: '13px',
    color: '#334155',
    backgroundColor: '#f1f5f9',
    padding: '4px 10px',
    borderRadius: '6px',
  },
  moreCount: {
    fontSize: '12px',
    color: '#64748b',
    padding: '4px 8px',
  },
  pubmedSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  pubmedCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#16a34a',
  },
  // Population styles
  frequencyContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  globalFreqSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
  },
  globalFreqItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  freqLabel: {
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center',
  },
  freqValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1e293b',
  },
  populationBars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  populationBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  popBarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  popName: {
    fontSize: '13px',
    color: '#475569',
  },
  popFreq: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1e293b',
    fontFamily: 'monospace',
  },
  popBarTrack: {
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  popBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease-out',
  },
  freqInterpretation: {
    display: 'flex',
    gap: '10px',
    padding: '14px',
    backgroundColor: '#fefce8',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#854d0e',
  },
  freqInterpretIcon: {
    fontSize: '16px',
  },
  // Transcript styles (now used in Overview tab)
  transcriptDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
    gap: '16px',
  },
  transcriptItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  transcriptLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 500,
  },
  transcriptValue: {
    fontSize: '14px',
    color: '#1e293b',
  },
  transcriptValueMono: {
    fontSize: '13px',
    color: '#1e293b',
    fontFamily: 'monospace',
    backgroundColor: '#f1f5f9',
    padding: '6px 10px',
    borderRadius: '6px',
    wordBreak: 'break-all',
  },
  transcriptLink: {
    fontSize: '14px',
    color: '#4f46e5',
    textDecoration: 'none',
  },
  quickLinks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
    gap: '10px',
  },
  quickLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#334155',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
    border: '1px solid #e2e8f0',
  },
  quickLinkIcon: {
    fontSize: '16px',
  },
};

export default VariantReport;
